import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, X, Rocket, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { onboarding as onboardingApi } from '@/lib/api';
import { startTour } from '@/components/GuidedTourOverlay';
import { toast } from 'sonner';

/**
 * OnboardingChecklist — first-run wizard that appears on the dashboard until
 * the firm has (1) branded the workspace, (2) verified their email domain,
 * (3) added a contact, (4) created a matter, and (5) invited a teammate.
 *
 * Fetches status on mount; each step has a link CTA to the relevant page.
 * Dismissable for 24h or permanently. Auto-hides when all steps are done.
 */
export default function OnboardingChecklist() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try { setData(await onboardingApi.status()); }
    catch { /* silent — the wizard is optional */ }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Re-check when the tab becomes visible again so completing a step in
  // another tab reflects here automatically.
  React.useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  if (loading || !data || !data.should_show) return null;

  const pct = Math.round((data.done_count / data.total) * 100);

  const dismiss = async () => {
    setBusy(true);
    try { await onboardingApi.dismiss(); setData({ ...data, should_show: false }); toast.success('Hidden for 24 hours'); }
    catch { toast.error('Could not dismiss'); }
    finally { setBusy(false); }
  };
  const done = async () => {
    setBusy(true);
    try { await onboardingApi.complete(); setData({ ...data, should_show: false }); }
    catch { toast.error('Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <Card
      className="relative overflow-hidden border-2 border-[#2ECC9A]/30 bg-gradient-to-br from-[#1A3A5C]/[0.03] via-white to-[#2ECC9A]/5"
      data-testid="onboarding-checklist"
    >
      {/* Corner sparkle motif */}
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#2ECC9A]/10 blur-2xl" aria-hidden />
      <CardContent className="relative p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1A3A5C] text-white">
              <Rocket className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-serif text-lg font-semibold text-[#1A3A5C]">Launch checklist</div>
                <span className="rounded-full border border-[#2ECC9A]/30 bg-[#2ECC9A]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1A3A5C]" data-testid="onboarding-progress">
                  {data.done_count}/{data.total}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Five quick wins to go from empty workspace to sending your first partner pack.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm" onClick={dismiss} disabled={busy}
              data-testid="onboarding-dismiss"
              className="h-8 text-[11px] text-slate-500 hover:text-[#1A3A5C]"
            >
              Snooze 24h
            </Button>
            <Button
              variant="ghost" size="icon" onClick={done} disabled={busy}
              data-testid="onboarding-hide-forever"
              title="Hide forever"
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={pct}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#2ECC9A] to-[#1A3A5C] transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>

        {/* Steps */}
        <div className="mt-4 grid gap-2">
          {data.steps.map((s, i) => (
            <div
              key={s.key}
              className={`group flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                s.done ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-white hover:border-[#2ECC9A]/40 hover:bg-slate-50'
              }`}
              data-testid={`onboarding-step-${s.key}`}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                {s.done
                  ? <CheckCircle2 className="h-6 w-6 text-emerald-500" data-testid={`onboarding-step-${s.key}-done`} />
                  : <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-[10px] font-semibold text-slate-400 group-hover:border-[#2ECC9A]">{i + 1}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-semibold ${s.done ? 'text-emerald-800' : 'text-[#1A3A5C]'}`}>
                  {s.title}
                </div>
                <div className="text-[11px] text-slate-500">{s.description}</div>
                {s.key === 'domain' && !s.done && s.status && s.status !== 'not_configured' && (
                  <div className="mt-0.5 text-[10px] uppercase tracking-wide text-amber-600">DNS status: {s.status}</div>
                )}
                {(s.key === 'contact' || s.key === 'matter' || s.key === 'team') && s.count > 0 && !s.done && (
                  <div className="mt-0.5 text-[10px] text-slate-400">{s.count} on file</div>
                )}
              </div>
              {!s.done && (
                <Link
                  to={s.cta_path}
                  onClick={() => {
                    if (s.tour_target) {
                      startTour({ testid: s.tour_target, tooltip: s.tour_tooltip, from: `onboarding-${s.key}` });
                    }
                  }}
                  data-testid={`onboarding-cta-${s.key}`}
                >
                  <Button size="sm" variant="outline" className="h-8 border-[#1A3A5C]/20 text-xs hover:bg-[#1A3A5C] hover:text-white">
                    {s.cta_label} <ChevronRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>

        {data.done_count === data.total - 1 && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-3 text-xs">
            <Sparkles className="h-4 w-4 text-[#2ECC9A]" />
            <span className="text-[#1A3A5C]">One more step and you&rsquo;re fully live — nice work.</span>
          </div>
        )}
        {busy && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm">
            <Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
